// ============================================
// FILE: frontend/src/api/messages.api.js
// PURPOSE: API calls for in-app messaging system
// ============================================

import apiClient from './client.js';

console.log('[messages.api] Initializing messages API');

// ============================================
// SECTION: SEND MESSAGE
// ============================================

/**
 * Send a message in a connection chat
 * @param {string} connectionId - Connection ID
 * @param {string} content - Message content (max 5000 chars)
 */
export const sendMessage = async (connectionId, content) => {
  console.log('[messages.api] 💬 Calling: POST /api/messages/send', {
    connectionId,
    contentLength: content.length
  });

  try {
    const response = await apiClient.post('/messages/send', {
      connectionId,
      content
    });
    console.log('[messages.api] ✅ Message sent:', {
      messageId: response.data.data?.id,
      createdAt: response.data.data?.createdAt
    });
    return response.data;
  } catch (error) {
    console.error('[messages.api] ❌ Failed to send message:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: FETCH MESSAGES (CHAT HISTORY)
// ============================================

/**
 * Get all messages in a connection (chat history)
 * @param {string} connectionId - Connection ID
 * @param {number} page - Page number (default 1)
 * @param {number} limit - Messages per page (default 50)
 */
export const getMessages = async (connectionId, page = 1, limit = 50) => {
  console.log('[messages.api] 📜 Calling: GET /api/messages/:connectionId', {
    connectionId,
    page,
    limit
  });

  try {
    const response = await apiClient.get(`/messages/${connectionId}`, {
      params: { page, limit }
    });
    console.log('[messages.api] ✅ Messages fetched:', {
      count: response.data.messages?.length,
      total: response.data.pagination?.total
    });
    return response.data;
  } catch (error) {
    console.error('[messages.api] ❌ Failed to fetch messages:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: GET UNREAD COUNT (SINGLE CONNECTION)
// ============================================

/**
 * Get unread message count for a specific connection
 * @param {string} connectionId - Connection ID
 */
export const getUnreadCount = async (connectionId) => {
  console.log('[messages.api] 🔔 Calling: GET /api/messages/:connectionId/unread', {
    connectionId
  });

  try {
    const response = await apiClient.get(`/messages/${connectionId}/unread`);
    console.log('[messages.api] ✅ Unread count retrieved:', {
      unreadCount: response.data.unreadCount
    });
    return response.data;
  } catch (error) {
    console.error('[messages.api] ❌ Failed to fetch unread count:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: GET TOTAL UNREAD MESSAGES
// ============================================

/**
 * Get total unread messages across all connections
 * Used for notification badge in user dashboard
 */
export const getTotalUnreadMessages = async () => {
  console.log('[messages.api] 📊 Calling: GET /api/messages/total/unread');

  try {
    const response = await apiClient.get('/messages/total/unread');
    console.log('[messages.api] ✅ Total unread retrieved:', {
      totalUnread: response.data.totalUnread,
      connectionsWithUnread: response.data.connectionsWithUnread?.length
    });
    return response.data;
  } catch (error) {
    console.error('[messages.api] ❌ Failed to fetch total unread:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: ADMIN VIEW CHAT
// ============================================

/**
 * Admin views full conversation between two parties (monitoring/support)
 * @param {string} connectionId - Connection ID
 */
export const adminViewChat = async (connectionId) => {
  console.log('[messages.api] 🔍 Calling: GET /api/messages/admin/:connectionId/view', {
    connectionId
  });

  try {
    const response = await apiClient.get(`/messages/admin/${connectionId}/view`);
    console.log('[messages.api] ✅ Admin chat view retrieved:', {
      messageCount: response.data.chat?.messageCount,
      lastMessage: response.data.chat?.lastMessage?.createdAt
    });
    return response.data;
  } catch (error) {
    console.error('[messages.api] ❌ Failed to view chat:', error.response?.data);
    throw error;
  }
};

// ============================================
// EXPORTS
// ============================================

export const messagesAPI = {
  sendMessage,
  getMessages,
  getUnreadCount,
  getTotalUnreadMessages,
  adminViewChat
};

export default messagesAPI;